import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellOff, CheckCheck, Trash2 } from 'lucide-react';
import {
  Button,
  Description,
  Drawer,
  EmptyState,
  Heading,
  Spinner,
  type OverlayTriggerState,
} from '@heroui/react';
import {
  deleteReadNotifications,
  getNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  notificationsKeys,
  type AppNotification,
} from '../../../api/notifications';
import { drawerBodyClassName, drawerFooterClassName, sideDrawerDialogClassName } from '../../../components/ui/side-drawer-classes';
import { StatusCallout } from '../../../components/ui/status-callout';
import { showErrorFromUnknown } from '../../../lib/toast';
import { formatDateTimeBR } from '../../../utils/date-utils';
import {
  getNotificationHref,
  getNotificationIcon,
  parseNotificationMetadata,
} from '../utils/notification-display';
import { PushPromptBanner } from './push-prompt-banner';
import { ExpandableImage } from '../../../components/ui/expandable-image';

interface NotificationsDrawerProps {
  state: OverlayTriggerState;
}

export function NotificationsDrawer({ state }: NotificationsDrawerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const listQuery = useQuery({
    queryKey: notificationsKeys.list(),
    queryFn: getNotifications,
    enabled: state.isOpen,
    staleTime: 0,
  });

  const invalidateNotifications = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: notificationsKeys.list() }),
      queryClient.invalidateQueries({ queryKey: notificationsKeys.unreadCount() }),
    ]);

  const markAsReadMutation = useMutation({
    mutationFn: markNotificationAsRead,
    onSuccess: invalidateNotifications,
    onError: showErrorFromUnknown,
  });

  const markAllMutation = useMutation({
    mutationFn: markAllNotificationsAsRead,
    onSuccess: async () => {
      await invalidateNotifications();
    },
    onError: showErrorFromUnknown,
  });

  const clearReadMutation = useMutation({
    mutationFn: deleteReadNotifications,
    onSuccess: async () => {
      await invalidateNotifications();
    },
    onError: showErrorFromUnknown,
  });

  const notifications = listQuery.data ?? [];
  const unreadCount = notifications.filter((item) => !item.read).length;
  const readCount = notifications.filter((item) => item.read).length;
  const busy = markAllMutation.isPending || clearReadMutation.isPending;

  const handleNotificationPress = async (notification: AppNotification) => {
    try {
      if (!notification.read) {
        await markAsReadMutation.mutateAsync(notification.publicId);
      }
      const href = getNotificationHref(notification);
      state.close();
      if (href) navigate(href);
    } catch {
      // onError do mutation já trata o toast
    }
  };

  return (
    <Drawer state={state}>
      <Drawer.Backdrop isDismissable>
        <Drawer.Content placement="right">
          <Drawer.Dialog className={sideDrawerDialogClassName}>
            <Drawer.Header>
              <Drawer.Heading>Notificações</Drawer.Heading>
              <Drawer.CloseTrigger />
            </Drawer.Header>

            <Drawer.Body className={`${drawerBodyClassName} gap-3 p-0`}>
              <PushPromptBanner enabled={state.isOpen} />

              {unreadCount > 0 ? (
                <div className="flex items-center justify-between gap-2 border-b border-separator px-4 py-3">
                  <Description>
                    {unreadCount === 1
                      ? '1 notificação não lida'
                      : `${unreadCount} notificações não lidas`}
                  </Description>
                  <Button
                    size="sm"
                    variant="ghost"
                    isDisabled={busy}
                    onPress={() => markAllMutation.mutate()}
                  >
                    {markAllMutation.isPending ? <Spinner size="sm" /> : <CheckCheck size={16} />}
                    Marcar todas
                  </Button>
                </div>
              ) : null}

              {listQuery.isError ? (
                <div className="p-4">
                  <StatusCallout status="danger">
                    Não foi possível carregar as notificações.
                  </StatusCallout>
                </div>
              ) : listQuery.isLoading ? (
                <div className="flex justify-center py-12">
                  <Spinner />
                </div>
              ) : notifications.length === 0 ? (
                <EmptyState className="flex flex-col items-center gap-2 py-16 text-center">
                  <BellOff size={40} className="text-muted" />
                  <Heading level={3}>Nenhuma notificação</Heading>
                  <Description>Quando houver avisos, eles aparecem aqui.</Description>
                </EmptyState>
              ) : (
                <ul className="divide-y divide-separator">
                  {notifications.map((notification) => (
                    <NotificationRow
                      key={notification.publicId}
                      notification={notification}
                      disabled={busy || markAsReadMutation.isPending}
                      onPress={() => handleNotificationPress(notification)}
                    />
                  ))}
                </ul>
              )}
            </Drawer.Body>

            <Drawer.Footer className={drawerFooterClassName}>
              <Button
                variant="danger-soft"
                className="flex-1"
                isDisabled={readCount === 0 || busy}
                onPress={() => clearReadMutation.mutate()}
              >
                {clearReadMutation.isPending ? <Spinner size="sm" /> : <Trash2 size={16} />}
                Limpar lidas
              </Button>
              <Button variant="secondary" className="flex-1" onPress={() => state.close()}>
                Fechar
              </Button>
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
}

function NotificationRow({
  notification,
  disabled,
  onPress,
}: {
  notification: AppNotification;
  disabled: boolean;
  onPress: () => void;
}) {
  const Icon = getNotificationIcon(notification.type);
  const unread = !notification.read;
  const meta = parseNotificationMetadata(notification.metadata);

  return (
    <li className={unread ? 'bg-accent/10' : ''}>
      <button
        type="button"
        disabled={disabled}
        onClick={onPress}
        className="flex w-full items-start gap-3 px-4 py-4 text-left transition-colors hover:bg-surface-secondary disabled:opacity-60"
      >
        <span
          className={`mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full ${
            unread ? 'bg-accent/15 text-accent' : 'bg-surface-secondary text-muted'
          }`}
        >
          <Icon size={16} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-start gap-2">
            {unread ? (
              <span className="mt-1.5 size-2 shrink-0 rounded-full bg-accent" />
            ) : null}
            <span
              className={`text-sm ${unread ? 'font-semibold text-foreground' : 'font-medium text-muted'}`}
            >
              {notification.title}
            </span>
          </span>
          <span className="mt-1 block whitespace-pre-line text-sm leading-relaxed text-muted">
            {notification.message}
          </span>
          {meta?.licensePlate ? (
            <span className="mt-1 block text-xs font-medium text-foreground">
              Placa: {meta.licensePlate}
            </span>
          ) : null}
          <span className="mt-1.5 block text-xs text-muted">
            {formatDateTimeBR(notification.createdAt)}
          </span>
        </span>
      </button>
      {meta?.photoUrl ? (
        <div className="px-4 pb-4 pl-[4.75rem]">
          <ExpandableImage
            src={meta.photoUrl}
            alt="Foto do mau uso reportado"
            className="max-h-32 w-full rounded-lg object-cover"
          />
        </div>
      ) : null}
    </li>
  );
}
